/**
 * Funções de acesso à API de billing admin.
 *
 * Todos os endpoints estão em `/api/v1/admin/billing/*` e requerem
 * usuário com `is_platform_admin=true`.
 */
import { httpClient } from '../../shared/http/client';
import type {
  BillingConfigPublic,
  BillingConfigUpdate,
  ModelPriceCreate,
  ModelPricePublic,
  MultiplierPublic,
  MultiplierUpdate,
  PlanChangePrice,
  PlanChangePriceResult,
  PlanCreate,
  PlanProvision,
  PlanPublic,
  PlanUpdate,
} from './types';

/** Retorna a configuração global de billing. */
export async function getBillingConfig(): Promise<BillingConfigPublic> {
  const res = await httpClient.get<BillingConfigPublic>('/api/v1/admin/billing/config');
  return res.data;
}

/** Atualiza (ou cria) a configuração global de billing. */
export async function updateBillingConfig(
  payload: BillingConfigUpdate,
): Promise<BillingConfigPublic> {
  const res = await httpClient.put<BillingConfigPublic>('/api/v1/admin/billing/config', payload);
  return res.data;
}

/** Lista todos os multiplicadores de crédito por source. */
export async function listMultipliers(): Promise<MultiplierPublic[]> {
  const res = await httpClient.get<MultiplierPublic[]>('/api/v1/admin/billing/multipliers');
  return res.data;
}

/** Atualiza o multiplicador de um source específico. */
export async function updateMultiplier(
  source: string,
  payload: MultiplierUpdate,
): Promise<MultiplierPublic> {
  const res = await httpClient.put<MultiplierPublic>(
    `/api/v1/admin/billing/multipliers/${source}`,
    payload,
  );
  return res.data;
}

/** Lista todos os planos do catálogo de billing. */
export async function listPlans(): Promise<PlanPublic[]> {
  const res = await httpClient.get<PlanPublic[]>('/api/v1/admin/billing/plans');
  return res.data;
}

/** Cria um novo plano no catálogo. */
export async function createPlan(payload: PlanCreate): Promise<PlanPublic> {
  const res = await httpClient.post<PlanPublic>('/api/v1/admin/billing/plans', payload);
  return res.data;
}

/** Atualiza um plano existente pelo key. */
export async function updatePlan(key: string, payload: PlanUpdate): Promise<PlanPublic> {
  const res = await httpClient.put<PlanPublic>(`/api/v1/admin/billing/plans/${key}`, payload);
  return res.data;
}

/** Remove um plano do catálogo pelo key. */
export async function deletePlan(key: string): Promise<void> {
  await httpClient.delete(`/api/v1/admin/billing/plans/${key}`);
}

/** Provisiona um plano novo: cria Product+Price no Stripe e grava o plano. */
export async function provisionPlan(payload: PlanProvision): Promise<PlanPublic> {
  const res = await httpClient.post<PlanPublic>('/api/v1/admin/billing/plans/provision', payload);
  return res.data;
}

/** Muda o preço de um plano: cria Price novo no Stripe e migra os assinantes atuais. */
export async function changePlanPrice(
  key: string,
  payload: PlanChangePrice,
): Promise<PlanChangePriceResult> {
  const res = await httpClient.post<PlanChangePriceResult>(
    `/api/v1/admin/billing/plans/${key}/change-price`,
    payload,
  );
  return res.data;
}

/** Lista todos os preços de modelos de IA. */
export async function listModelPrices(): Promise<ModelPricePublic[]> {
  const res = await httpClient.get<ModelPricePublic[]>('/api/v1/admin/billing/prices');
  return res.data;
}

/** Registra um novo preço de modelo (append-only). */
export async function createModelPrice(body: ModelPriceCreate): Promise<ModelPricePublic> {
  const res = await httpClient.post<ModelPricePublic>('/api/v1/admin/billing/prices', body);
  return res.data;
}
